// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useEffect, useMemo, useRef, useState } from "react";
import "./styles/tokens.css";
import "./styles/app.css";
import "../components/auth/Auth.css";
import "./components/VaccineDashboard/styles/vaccine-demo.css";
import DecisionTreeBuilder from "./components/DecisionTree/DecisionTreeBuilder";
import VaccineDashboard from "./components/VaccineDashboard/VaccineDashboardContainer";
import { useAutoInitializeTree } from "./hooks/useAutoInitializeTree";
import {
  prepareComputationData,
  syncTreeParametersToVaccine,
} from "./facade/vaccineFacade";
import ActivityDrawer from "./components/VaccineDashboard/components/ActivityDrawer";
import { QueryProvider } from "./providers/QueryProvider";
import { EvidenceDrawer } from "./components/VaccineDashboard/components/EvidenceDrawer";
import { VaccineDashboardHeaderConnected } from "./components/VaccineDashboard/components/VaccineDashboardHeaderConnected";
import { AuthGuard } from "../components/auth/AuthGuard";
import { UserProfile } from "../components/auth/UserProfile";

type RouteKey = "dashboard" | "tree";

const ROUTE_HASH: Record<RouteKey, string> = {
  dashboard: "#dashboard",
  tree: "#tree",
};

const ROUTE_LABEL: Record<RouteKey, string> = {
  dashboard: "Dashboard",
  tree: "Decision Tree",
};

const KNOWN_HASHES: ReadonlySet<string> = new Set<string>([
  "",
  ROUTE_HASH.dashboard,
  ROUTE_HASH.tree,
]);

const getRouteFromHash = (hash: string): RouteKey => {
  if (hash === ROUTE_HASH.tree) return "tree";
  if (!KNOWN_HASHES.has(hash)) {
    // Unknown hash → log once for support diagnostics. The effect that mirrors
    // the active route to window.location.hash will normalize the URL bar.
    console.warn(
      `[vaccine-client] Unknown route hash "${hash}", normalizing to ${ROUTE_HASH.dashboard}`
    );
  }
  return "dashboard";
};

const App: React.FC = () => {
  // Auto-initialize decision tree from vaccine dashboard inputs
  useAutoInitializeTree();

  const [activeRoute, setActiveRoute] = useState<RouteKey>(() =>
    getRouteFromHash(window.location.hash || ROUTE_HASH.dashboard)
  );

  const prevRouteRef = useRef<RouteKey>(activeRoute);

  useEffect(() => {
    const handleHashChange = () => {
      setActiveRoute(getRouteFromHash(window.location.hash));
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    const expectedHash = ROUTE_HASH[activeRoute];
    if (window.location.hash !== expectedHash) {
      window.location.hash = expectedHash;
    }
  }, [activeRoute]);

  // Bi-directional parameter sync between vaccine dashboard and decision tree
  useEffect(() => {
    const prevRoute = prevRouteRef.current;

    // Sync vaccine → tree when entering tree view
    if (activeRoute === "tree" && prevRoute !== "tree") {
      prepareComputationData();
    }

    // Sync tree → vaccine when leaving tree view
    if (prevRoute === "tree" && activeRoute !== "tree") {
      syncTreeParametersToVaccine();
    }

    prevRouteRef.current = activeRoute;
  }, [activeRoute]);

  const navButtons = useMemo<RouteKey[]>(() => ["dashboard", "tree"], []);

  return (
    <AuthGuard>
      <QueryProvider>
        <div className="vaccine-client-app">
          <ActivityDrawer />
          <header className="vaccine-client-app__header">
            <div className="vaccine-demo__title">
              <h1>
                <span className="brand-prefix">EASY</span>
                <span className="brand-suffix">HTA</span>
              </h1>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                height: '36px'
              }}>
                <img
                  src={`${process.env.PUBLIC_URL || ''}/logos/pfizer-logo-white.png`}
                  alt="Pfizer"
                  style={{ height: '36px', width: 'auto', display: 'block' }}
                />
              </div>
            </div>

            <VaccineDashboardHeaderConnected />
            
            <UserProfile className="vaccine-client-app__user-profile" />
            
            <nav
              className="vaccine-client-app__nav"
              aria-label="Primary navigation"
            >
              {navButtons.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`vaccine-client-app__nav-button${
                    key === activeRoute
                      ? " vaccine-client-app__nav-button--active"
                      : ""
                  }`}
                  onClick={() => setActiveRoute(key)}
                >
                  {ROUTE_LABEL[key]}
                </button>
              ))}
            </nav>
          </header>

          <main className="vaccine-client-app__main">
            {activeRoute === "tree" ? (
              <DecisionTreeBuilder />
            ) : (
              <VaccineDashboard />
            )}
          </main>
          <EvidenceDrawer />
        </div>
      </QueryProvider>
    </AuthGuard>
  );
};

export default App;
